/**
 * usePageTimeTracking Hook
 *
 * Tracks time spent on each page
 * Automatically sends analytics event when user leaves the page
 *
 * Safety features:
 * - Non-blocking tracking
 * - Automatic cleanup
 * - Graceful error handling
 * - Debounced to prevent spam
 */

import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import posthog from 'posthog-js';
import { safeAnalytics, getPageCategory, getPageViewType } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import type { PageCategory } from '../../types/analytics';

interface PageTimeTrackingOptions {
  /**
   * Minimum time threshold (in seconds) before tracking
   * This prevents tracking very brief page visits
   */
  minTimeThreshold?: number;
}

export function usePageTimeTracking(options: PageTimeTrackingOptions = {}) {
  const { minTimeThreshold = 2 } = options;
  const location = useLocation();
  const pageEnterTimeRef = useRef<number>(Date.now());
  const currentPathRef = useRef<string>(location.pathname);
  const pageCategoryRef = useRef<PageCategory>(getPageCategory(location.pathname));

  // Send final Page Time event and mark as flushed.
  // Stable callback: only recreated when minTimeThreshold changes (refs are stable).
  const flushPageTime = useCallback(() => {
    if (!pageEnterTimeRef.current) return;

    const timeOnPage = Math.floor((Date.now() - pageEnterTimeRef.current) / 1000);
    if (timeOnPage >= minTimeThreshold) {
      safeAnalytics.trackImmediate(ANALYTICS_EVENTS.PAGE_TIME, {
        page_category: pageCategoryRef.current,
        page_path: currentPathRef.current,
        time_on_page_seconds: timeOnPage,
        timestamp: new Date().toISOString(),
      });
    }

    // Mark as flushed — prevents double-send in cleanup effect
    pageEnterTimeRef.current = 0;
  }, [minTimeThreshold]);

  // Register pre-logout callback so final page time is sent while user
  // is still identified (before posthog.reset() clears the identity).
  useEffect(() => {
    safeAnalytics.registerPreLogoutCallback(flushPageTime);
    return () => {
      safeAnalytics.unregisterPreLogoutCallback(flushPageTime);
    };
  }, [flushPageTime]);

  useEffect(() => {
    // Update page tracking on route change
    const newPath = location.pathname;
    const newCategory = getPageCategory(newPath);

    // If path changed, track time on previous page
    if (currentPathRef.current !== newPath && pageEnterTimeRef.current) {
      const timeOnPage = Math.floor((Date.now() - pageEnterTimeRef.current) / 1000);

      // Only track if user spent more than minimum threshold
      if (timeOnPage >= minTimeThreshold) {
        try {
          safeAnalytics.track(ANALYTICS_EVENTS.PAGE_TIME, {
            page_category: pageCategoryRef.current,
            page_path: currentPathRef.current,
            time_on_page_seconds: timeOnPage,
            timestamp: new Date().toISOString(),
          });
        } catch (error) {
          console.warn('Page time tracking error:', error);
        }
      }

      // Reset for new page
      pageEnterTimeRef.current = Date.now();
      currentPathRef.current = newPath;
      pageCategoryRef.current = newCategory;
    }

    // Track time on page when component unmounts or page unloads.
    // If pre-logout callback already flushed (pageEnterTimeRef === 0), this is a no-op.
    return () => {
      try {
        flushPageTime();
      } catch (error) {
        console.warn('Page time tracking cleanup error:', error);
      }
    };
  }, [location.pathname, minTimeThreshold, flushPageTime]);

  // Track page view — fires both our custom event and a manual $pageview
  // so PostHog Web Analytics works with authenticated-only data.
  // (Built-in capture_pageview is disabled to avoid anonymous pre-auth inflation.)
  useEffect(() => {
    try {
      safeAnalytics.track(ANALYTICS_EVENTS.PAGE_VIEWED, {
        page_category: getPageCategory(location.pathname),
        page_view_type: getPageViewType(location.pathname),
        page_path: location.pathname,
        // Host only: a referring URL's path and query are someone else's.
        referrer: referrerHost(document.referrer),
        timestamp: new Date().toISOString(),
      });

      // Keep page context super properties in sync with current route
      safeAnalytics.registerContext({
        page_path: location.pathname,
        page_category: getPageCategory(location.pathname),
      });

      // Manual $pageview for PostHog Web Analytics (post-auth only)
      posthog.capture('$pageview');
    } catch (error) {
      console.warn('Page view tracking error:', error);
    }
  }, [location.pathname]);
}

function referrerHost(referrer: string): string {
  try {
    return referrer ? new URL(referrer).host : '';
  } catch {
    return '';
  }
}
